import { CommonModule } from '@angular/common';
import { tagStyle } from '@utils/tag-color.util';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { FilterService } from '@services/filter.service';
import { LngLatBounds } from 'maplibre-gl';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { DeviceViewModel } from '../../device-info-list-table/device-info-list-table.component';
import { MatCard } from '@angular/material/card';

interface DeviceInfoMapListTableFilter {
  deviceName?: string;
  serialNo?: string;
  tag?: string;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'DeviceInfoMapListTable';

@Component({
  selector: 'app-device-info-map-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    DirectivesModule,
    MatCard,
  ],
  templateUrl: './device-info-map-list-table.component.html',
  styleUrl: './device-info-map-list-table.component.scss',
})
export class DeviceInfoMapListTableComponent
  implements OnChanges, OnInit, AfterViewInit
{
  public readonly tagStyle = tagStyle;
  /** テーブルの各行を参照するためのプロパティ */
  @ViewChildren('row') rows!: QueryList<ElementRef>;

  /** デバイス情報 */
  @Input({ required: true }) deviceList: DeviceViewModel[] = [];

  /** マップ表示範囲 */
  @Input({ required: true }) mapBounds: LngLatBounds | undefined;

  /** 選択されているデバイス情報 */
  @Input() public selectedDevice?: DeviceViewModel;

  /** 選択行変更イベント */
  @Output() deviceSelectionChange = new EventEmitter<DeviceViewModel>();

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = ['deviceName', 'serialNo', 'tag'];

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<DeviceViewModel>([]);

  /** デバイス名検索キーワード */
  public deviceNameKeyword = '';

  /** シリアル番号検索キーワード */
  public serialNoKeyword = '';

  /** タグ検索キーワード */
  public tagKeyword = '';

  /** 選択行 */
  public selectedRow: DeviceViewModel | undefined;

  /** 初期ソート値 */
  public initSortState: Sort = {
    active: 'deviceName',
    direction: 'asc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  constructor(
    private filterService: FilterService<
      DeviceInfoMapListTableFilter,
      DeviceViewModel
    >,
  ) {
    this.filterService.setSessionStorageKey(pageId);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDevice'] !== undefined) {
      const selectedRow: DeviceViewModel =
        changes['selectedDevice'].currentValue;
      this.selectedRow = selectedRow;
      this.scrollToSelectedDevice();
    }

    if (changes['deviceList'] !== undefined) {
      const deviceList: DeviceViewModel[] = changes['deviceList'].currentValue;
      this.dataSource.data = deviceList;
    }

    if (changes['mapBounds'] !== undefined) {
      const filteredDeviceList = this.filterDevicesByBounds(
        this.deviceList,
        changes['mapBounds'].currentValue,
      );
      this.dataSource.data = filteredDeviceList;
    }
  }

  public ngOnInit(): void {
    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit(): void {
    // DataSourceにソートを適用
    this.dataSource.sort = this.sort;

    // ソートの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort);

    // ソートの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };
      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // フィルターの設定
    this.dataSource.filterPredicate = (element: DeviceViewModel) => {
      let deviceName = true;
      if (this.deviceNameKeyword) {
        // deviceNameKeywordが空でない場合のみフィルタリングを行う
        deviceName =
          element.deviceName
            .trim()
            .toLowerCase()
            .indexOf(this.deviceNameKeyword.toLowerCase()) > -1;
      }
      let serialNo = true;
      if (this.serialNoKeyword) {
        // serialNoKeywordが空でない場合のみフィルタリングを行う
        serialNo =
          element.serialNo
            .trim()
            .toLowerCase()
            .indexOf(this.serialNoKeyword.toLowerCase()) > -1;
      }
      let tag = true;
      if (this.tagKeyword) {
        // tagKeywordが空でない場合のみフィルタリングを行う
        tag = element.tag.some((tagItem) =>
          tagItem.trim().toLowerCase().includes(this.tagKeyword.toLowerCase()),
        );
        // 並べ替え処理
        if (tag) {
          const sortedTags = [...element.tag].sort((a, b) =>
            this.compareTags(a, b),
          );
          element.tag = sortedTags;
        }
      }

      // フィルター条件を保持
      this.saveFiltersToStorage();

      return deviceName && serialNo && tag;
    };

    // 描画毎にフィルターを適用
    this.applyFilter();
  }

  /**
   * テーブル行を選択する。
   * @param selectedRow 選択されたテーブル行
   */
  public selectDevice(selectedRow: DeviceViewModel): void {
    // 既に選択済みの行が再度選択された場合は選択状態を解除する
    if (selectedRow.serialNo === this.selectedRow?.serialNo) {
      this.selectedRow = undefined;
    } else {
      this.selectedRow = selectedRow;
    }

    this.deviceSelectionChange.emit(this.selectedRow);
  }

  /**
   * 地図上で指定された範囲内のデバイスにフィルタリングする。
   * @param deviceList デバイス一覧
   * @param bounds 地図上で指定された範囲
   * @returns フィルタリングされたデバイス一覧
   */
  public filterDevicesByBounds(
    deviceList: DeviceViewModel[],
    bounds: LngLatBounds,
  ): DeviceViewModel[] {
    const filteredDeviceList = deviceList.filter((device) => {
      // 経度・緯度の情報がないデバイスは除去
      if (
        device.location?.longitude == null ||
        device.location?.latitude == null
      ) {
        return false;
      }

      const isWithinBounds =
        device.location.longitude >= bounds._sw.lng &&
        device.location.longitude <= bounds._ne.lng &&
        device.location.latitude >= bounds._sw.lat &&
        device.location.latitude <= bounds._ne.lat;
      return isWithinBounds;
    });
    return filteredDeviceList;
  }

  /**
   * マウスオーバー時にポインターと背景色を変更する。
   * @param event MouseEvent
   */
  public onMouseEnter(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = 'pointer';
    target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
  }

  /**
   * マウスを離れた際元のスタイルに変更する。
   * @param event MouseEvent
   */
  public onMouseLeave(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = '';
    target.style.backgroundColor = '';
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * デバイス名でのフィルタリングをクリアする。
   */
  public clearDeviceNameFilter(): void {
    // フィルタ条件をクリア
    this.deviceNameKeyword = '';

    this.applyFilter();
  }

  /**
   * シリアル番号でのフィルタリングをクリアする。
   */
  public clearSerialNoFilter(): void {
    // フィルタ条件をクリア
    this.serialNoKeyword = '';

    this.applyFilter();
  }

  /**
   * タグでのフィルタリングをクリアする。
   */
  public clearTagFilter(): void {
    // フィルタ条件をクリア
    this.tagKeyword = '';

    this.applyFilter();
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(): void {
    const filters = this.filterService.getFiltersFromSessionStorage();
    this.deviceNameKeyword = filters.deviceName ?? '';
    this.serialNoKeyword = filters.serialNo ?? '';
    this.tagKeyword = filters.tag || '';
    this.sortState = filters.sortState;
  }

  /**
   * フィルター条件をセッションストレージに保存する。
   */
  private saveFiltersToStorage(): void {
    const filterList: DeviceInfoMapListTableFilter = {
      deviceName: this.deviceNameKeyword,
      serialNo: this.serialNoKeyword,
      tag: this.tagKeyword,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }

  /**
   * タグを比較してソートする。
   * @param a - 比較する最初のタグ
   * @param b - 比較する2番目のタグ
   * @returns
   */
  private compareTags(a: string, b: string): number {
    const aLower = a.trim().toLowerCase();
    const bLower = b.trim().toLowerCase();
    return aLower.localeCompare(bLower);
  }

  /**
   * 選択されたデバイスの位置までスクロールする。
   */
  private scrollToSelectedDevice(): void {
    const index = this.dataSource.data.findIndex(
      (device) => device.serialNo === this.selectedDevice?.serialNo,
    );
    if (index !== -1) {
      const row = this.rows.toArray()[index];
      if (row) {
        row.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }
}
